"use client";

import { useCallback, useState } from "react";

import { useStudio } from "@/contexts/studio-context";
import { useNotationSettings } from "@/hooks/use-notation";
import type { NoteNotation } from "@/lib/music/notation";
import { Group, Line, SectionHeader, Switch } from "./atoms";
import { SET } from "./palette";

type NotationOption = {
  id: NoteNotation;
  label: string;
  hint: string;
};

// Options are labelled with the thing itself rather than with the name of a
// convention — "Do Ré Mi" tells you what you'll get, "solfège" doesn't.
const NOTE_OPTIONS: readonly NotationOption[] = [
  {
    id: "latin",
    label: "Do Ré Mi",
    hint: "Notation française, celle des comptines et de l'école",
  },
  {
    id: "english",
    label: "C D E",
    hint: "Notation internationale, celle des partitions et des tutos",
  },
];

const CHORD_OPTIONS: readonly NotationOption[] = [
  {
    id: "english",
    label: "Cmaj7",
    hint: "Symboles internationaux, identiques aux grilles d'accords",
  },
  {
    id: "latin",
    label: "Do maj7",
    hint: "Symboles en notation française",
  },
];

export function NotationSection() {
  const {
    noteNotation,
    chordNotation,
    chordLocked,
    noteNamesOnScore,
    setNoteNotation,
    setChordNotation,
    setNoteNamesOnScore,
  } = useNotationSettings();
  const { traceVisible, setTraceVisible } = useLiveTraceVisibility();

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <SectionHeader kicker="Notation" />

      <Group>
        <Line
          label="Nom des notes"
          hint="Sur le clavier, les accords, les gammes et les grilles"
          control={
            <NotationPicker
              ariaLabel="Nom des notes"
              options={NOTE_OPTIONS}
              value={noteNotation}
              onChange={setNoteNotation}
            />
          }
        />

        <Line
          withDivider={false}
          label="Symboles d'accords"
          hint={
            chordLocked
              ? "Suit le nom des notes tant qu'il est en C D E"
              : "Les grilles et tutos du web utilisent les symboles internationaux"
          }
          control={
            <NotationPicker
              ariaLabel="Symboles d'accords"
              options={CHORD_OPTIONS}
              value={chordLocked ? "english" : chordNotation}
              onChange={setChordNotation}
              disabled={chordLocked}
            />
          }
        />

        <Line
          label="Nom des notes sur la partition"
          hint={
            noteNamesOnScore
              ? "Chaque note de la main gauche porte le nom de sa touche"
              : "La portée se lit seule, par sa clé et ses altérations"
          }
          control={
            <Switch
              checked={noteNamesOnScore}
              onChange={setNoteNamesOnScore}
              label="Nom des notes sur la partition"
            />
          }
        />

        <Line
          label="Ma trace"
          hint="Les notes que vous jouez restent dessinées à côté du morceau"
          control={
            <Switch
              checked={traceVisible}
              onChange={setTraceVisible}
              label="Ma trace"
            />
          }
        />
      </Group>
    </section>
  );
}

/**
 * Affichage de la piste live du clavier sur le piano-roll.
 *
 * La voie est cumulative par construction : en jeu libre, revoir ce qu'on
 * vient de jouer *est* le produit. Passé quelques passes, la même voie devient
 * une superposition de fantômes gris qui se lit mal contre la main gauche —
 * d'où ce commutateur, seule façon de la retirer depuis que le panneau
 * « Pistes » a quitté la sidebar.
 *
 * `liveLaneMode` n'est pas un `Observable` (cf. `Studio.setLiveLaneMode`) :
 * l'overlay étant démonté à la fermeture, le relire au montage suffit. Le
 * parcours guidé pose aussi ce mode (`'trace'` après une passe) et reprend
 * donc la main sur ce réglage, ce qui est le comportement voulu : la trace du
 * parcours est un autre objet que la voie live du jeu libre.
 */
function useLiveTraceVisibility(): {
  traceVisible: boolean;
  setTraceVisible: (visible: boolean) => void;
} {
  const { studio } = useStudio();
  const [traceVisible, setVisible] = useState(
    () => (studio?.liveLaneMode ?? "notes") !== "hidden",
  );

  // Le studio peut arriver après le premier rendu, d'où la resynchro pendant
  // le rendu plutôt que dans un effet (qui afficherait d'abord la valeur par
  // défaut avant de se corriger).
  const [lastStudio, setLastStudio] = useState(studio);

  if (lastStudio !== studio) {
    setLastStudio(studio);
    setVisible((studio?.liveLaneMode ?? "notes") !== "hidden");
  }

  const setTraceVisible = useCallback(
    (visible: boolean) => {
      setVisible(visible);
      studio?.setLiveLaneMode(visible ? "notes" : "hidden");
    },
    [studio],
  );

  return { traceVisible, setTraceVisible };
}

interface NotationPickerProps {
  ariaLabel: string;
  options: readonly NotationOption[];
  value: NoteNotation;
  onChange: (next: NoteNotation) => void;
  disabled?: boolean;
}

function NotationPicker({
  ariaLabel,
  options,
  value,
  onChange,
  disabled = false,
}: NotationPickerProps) {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      style={{
        display: "inline-flex",
        gap: 6,
        opacity: disabled ? 0.45 : 1,
        transition: "opacity 150ms",
      }}
    >
      {options.map((option) => {
        const selected = option.id === value;

        return (
          <button
            key={option.id}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={disabled}
            title={option.hint}
            onClick={() => onChange(option.id)}
            style={{
              padding: "5px 12px",
              borderRadius: 8,
              border: `1px solid ${selected ? SET.violetGlow : SET.border}`,
              background: selected ? SET.violetSoft : "transparent",
              color: selected ? SET.text : SET.textDim,
              fontSize: 12,
              cursor: disabled ? "not-allowed" : "pointer",
              transition: "background 150ms, border-color 150ms, color 150ms",
              boxShadow: selected ? `0 0 8px ${SET.violetGlow}` : "none",
            }}
            onMouseEnter={(e) => {
              if (!selected && !disabled) {
                e.currentTarget.style.color = SET.text;
                e.currentTarget.style.borderColor = SET.borderHi;
              }
            }}
            onMouseLeave={(e) => {
              if (!selected && !disabled) {
                e.currentTarget.style.color = SET.textDim;
                e.currentTarget.style.borderColor = SET.border;
              }
            }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
